<template>
  <div class="account-page" :class="{ 'account-page--mobile': !isPC }">
    <section v-for="section in accountSections" :key="section.title" class="account-section">
      <h4 class="section-title">{{ section.title }}</h4>

      <div class="account-card-list">
        <article v-for="item in section.items" :key="item.key" class="account-card">
          <div class="account-card-main">
            <span class="account-icon-wrap" :class="item.iconClass">
              <SvgIcon :name="item.icon" :size="24" />
            </span>

            <div class="account-copy">
              <span class="account-title">{{ item.title }}</span>
              <span
                v-if="item.initializing"
                class="account-description-placeholder"
                aria-hidden="true"
              />
              <span v-else class="account-description">{{ item.description }}</span>
            </div>
          </div>

          <span
            v-if="item.initializing"
            class="account-action-loading"
            role="status"
            aria-label="加载中"
          >
            <el-icon class="is-loading"><Loading /></el-icon>
          </span>
          <button
            v-else
            type="button"
            class="account-action"
            :disabled="item.loading"
            @click="item.onClick"
          >
            {{ item.loading ? '处理中…' : item.actionText }}
          </button>
        </article>
      </div>
    </section>
  </div>

  <PhoneBindingGuard ref="phoneBindingGuardRef" />

  <el-dialog
    v-model="phoneDialog.visible"
    :class="[
      'settings-account-dialog',
      {
        'settings-account-dialog--mobile': !isPC,
        'phone-binding-dialog': phoneDialog.stage === 'bind'
      }
    ]"
    :title="phoneDialogTitle"
    :width="phoneDialog.stage === 'bind' ? '360px' : '400px'"
    align-center
    @closed="resetPhoneDialog"
  >
    <template v-if="phoneDialog.stage === 'unbind'">
      <el-alert
        class="phone-verification-alert"
        type="warning"
        title="更换手机号前，需要先验证原手机号"
        :closable="false"
        show-icon
      />
      <el-form label-position="top" @submit.prevent>
        <el-form-item label="原手机号">
          <el-input :model-value="formatPhone(accountInfo.phone)" disabled />
        </el-form-item>
        <el-form-item label="验证码">
          <div class="sms-row">
            <el-input
              v-model="phoneUnbind.code"
              placeholder="请输入原手机号验证码"
              maxlength="6"
              inputmode="numeric"
            />
            <el-button
              class="sms-button"
              :disabled="!!phoneUnbind.countdown"
              :loading="phoneUnbind.sendingCode"
              @click="sendUnbindPhoneCode"
            >
              {{ phoneUnbind.countdown ? `${phoneUnbind.countdown}s后重新获取` : '获取验证码' }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </template>

    <el-form v-else class="phone-binding-form" @submit.prevent="submitChangePhone">
      <p class="phone-binding-description">绑定手机后可以更方便的登录、管理账号</p>
      <VueTelInput
        :key="phoneDialog.inputKey"
        v-model="phoneDialog.newPhone"
        class="phone-number-input"
        mode="national"
        :auto-default-country="false"
        :auto-format="false"
        :default-country="phoneDialog.defaultCountry"
        :dropdown-options="phoneDropdownOptions"
        :input-options="phoneInputOptions"
        :preferred-countries="preferredPhoneCountries"
        valid-characters-only
        @country-changed="handlePhoneCountryChanged"
      />
      <div class="phone-binding-sms-row">
        <el-input
          v-model="phoneDialog.code"
          class="phone-binding-code"
          placeholder="请输入验证码"
          maxlength="6"
          inputmode="numeric"
        />
        <el-button
          class="phone-binding-sms-button"
          :disabled="!!phoneDialog.countdown"
          :loading="phoneDialog.sendingCode"
          @click="sendSms"
        >
          {{ phoneDialog.countdown ? `${phoneDialog.countdown}s` : '获取验证码' }}
        </el-button>
      </div>
      <el-button
        class="phone-binding-submit"
        type="primary"
        native-type="submit"
        :loading="phoneDialog.submitting"
      >
        确认
      </el-button>
    </el-form>

    <template #footer>
      <el-button v-if="phoneDialog.stage === 'unbind'" @click="phoneDialog.visible = false">
        取消
      </el-button>
      <el-button
        v-if="phoneDialog.stage === 'unbind'"
        type="primary"
        :loading="phoneUnbind.submitting"
        :disabled="!phoneUnbind.code"
        @click="verifyUnbindPhoneCode"
      >
        验证并继续
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    v-model="passwordDialog.visible"
    :class="['settings-account-dialog', { 'settings-account-dialog--mobile': !isPC }]"
    :title="accountInfo.hasPassword ? '更换密码' : '设置密码'"
    width="400px"
    align-center
    @closed="resetPasswordDialog"
  >
    <el-form label-position="top" @submit.prevent>
      <el-form-item v-if="accountInfo.hasPassword" label="原密码">
        <el-input
          v-model="passwordDialog.oldPassword"
          type="password"
          placeholder="请输入原密码"
          show-password
        />
      </el-form-item>
      <el-form-item label="新密码">
        <el-input
          v-model="passwordDialog.newPassword"
          type="password"
          placeholder="请输入新密码（6-20位）"
          maxlength="20"
          show-password
        />
      </el-form-item>
      <el-form-item label="确认新密码">
        <el-input
          v-model="passwordDialog.confirmPassword"
          type="password"
          placeholder="请再次输入新密码"
          maxlength="20"
          show-password
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="passwordDialog.visible = false">取消</el-button>
      <el-button
        type="primary"
        :loading="passwordDialog.submitting"
        :disabled="!passwordDialog.newPassword || !passwordDialog.confirmPassword"
        @click="submitChangePassword"
      >
        {{ accountInfo.hasPassword ? '确认更换' : '确认设置' }}
      </el-button>
    </template>
  </el-dialog>

  <el-dialog
    v-model="thirdAccountDialog.visible"
    :class="[
      'settings-account-dialog',
      'third-account-dialog',
      { 'settings-account-dialog--mobile': !isPC }
    ]"
    :title="`绑定${thirdAccountName}`"
    width="360px"
    align-center
    @close="stopThirdAccountPolling"
    @closed="resetThirdAccountDialog"
  >
    <div class="third-account-content">
      <div class="third-account-qrcode">
        <img
          v-if="thirdAccountDialog.qrUrl"
          :src="thirdAccountDialog.qrUrl"
          :alt="`${thirdAccountName}绑定二维码`"
        />
        <div v-if="thirdAccountDialog.expired" class="third-account-expired">
          <strong>二维码已过期</strong>
          <span>请关闭后重新获取</span>
        </div>
      </div>
      <p>
        请使用{{ thirdAccountName }}扫描二维码
        <span>完成授权后将自动绑定</span>
      </p>
    </div>

    <template #footer>
      <el-button @click="thirdAccountDialog.visible = false">关闭</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, reactive, ref } from 'vue';
  import { ElMessage, ElMessageBox } from 'element-plus';
  import { Loading } from '@element-plus/icons-vue';
  import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
  import { VueTelInput, type CountryObject } from 'vue-tel-input';
  import 'vue-tel-input/vue-tel-input.css';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import PhoneBindingGuard from '@/components/common/PhoneBindingGuard.vue';
  import { useViewportAccessGate } from '@/hooks/useViewportAccessGate';
  import { userApis, type ThirdAccountInfo } from '@/axiosModel';
  import type { ApiResponse } from '@/axiosModel/Model.class';
  import { useUserStore } from '@/store';
  import '@/utils/tn/css/style.css';
  import '@/utils/tn/js/tn_code.js';

  type ThirdAccountKey = 'wechat' | 'qq';

  interface ThirdAccountBindData {
    url?: string;
    expireTime?: number | string;
    sessionId?: string;
  }

  interface PhoneUnbindCheckData {
    checkCode?: string;
  }

  interface CountdownState {
    countdown: number;
    sendingCode: boolean;
  }

  interface AccountActionItem {
    key: string;
    icon: string;
    iconClass?: string;
    title: string;
    description: string;
    actionText: string;
    initializing?: boolean;
    loading?: boolean;
    onClick: () => void | Promise<void>;
  }

  interface AccountSection {
    title: string;
    items: AccountActionItem[];
  }

  const CHANGE_PHONE_OPERATE_ID = 4;
  const SMS_COUNTDOWN_SECONDS = 90;
  const THIRD_OPERATE_BIND = 0;
  const THIRD_OPERATE_UNBIND = 1;
  const THIRD_TYPE_WECHAT = 6;
  const THIRD_TYPE_QQ = 1;
  const THIRD_POLL_INTERVAL = 2000;
  const DEFAULT_THIRD_QR_EXPIRE_SECONDS = 300;
  const DEFAULT_AREA_CODE = '86';
  const DEFAULT_PHONE_COUNTRY = 'CN';

  const phoneDropdownOptions = {
    showDialCodeInList: true,
    showDialCodeInSelection: true,
    showFlags: false,
    showSearchBox: true,
    searchBoxPlaceholder: '搜索国家或区号'
  };
  const phoneInputOptions = {
    autocomplete: 'tel-national',
    inputmode: 'numeric' as const,
    maxlength: 20,
    placeholder: '请输入手机号'
  };
  const preferredPhoneCountries = ['CN', 'HK', 'MO', 'TW', 'US'];

  const userStore = useUserStore();
  const { isPC } = useViewportAccessGate();
  const thirdInfoLoading = ref(true);
  const phoneBindingGuardRef = ref<InstanceType<typeof PhoneBindingGuard>>();
  const thirdPartyLoading = reactive<Record<ThirdAccountKey, boolean>>({
    wechat: false,
    qq: false
  });

  const accountInfo = reactive({
    phone: userStore.userData.mobilePhone || userStore.userData.phone || '',
    hasPassword: userStore.userData.hasPassword === 1,
    wechatBound: false,
    wechatName: '',
    qqBound: false,
    qqName: ''
  });

  const phoneDialog = reactive({
    visible: false,
    stage: 'bind' as 'unbind' | 'bind',
    mode: 'bind' as 'bind' | 'change',
    inputKey: 0,
    areaCode: DEFAULT_AREA_CODE,
    countryCode: DEFAULT_PHONE_COUNTRY,
    defaultCountry: DEFAULT_PHONE_COUNTRY as string | number,
    newPhone: '',
    code: '',
    countdown: 0,
    sendingCode: false,
    submitting: false
  });

  const phoneUnbind = reactive({
    code: '',
    checkCode: '',
    countdown: 0,
    sendingCode: false,
    submitting: false
  });

  const phoneCaptcha = reactive({
    account: '',
    areaCode: 0,
    verified: false
  });

  const passwordDialog = reactive({
    visible: false,
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
    submitting: false
  });

  const thirdAccountDialog = reactive({
    visible: false,
    type: 'wechat' as ThirdAccountKey,
    qrUrl: '',
    sessionId: '',
    expiresAt: 0,
    expired: false
  });

  let smsTimer: ReturnType<typeof setInterval> | undefined;
  let thirdAccountPollTimer: ReturnType<typeof setTimeout> | undefined;
  let thirdAccountAuthTimer: ReturnType<typeof setInterval> | undefined;
  let cancelThirdAccountPollRequest: (() => void) | undefined;
  let thirdAccountAuthWindow: Window | null = null;

  const thirdAccountName = computed(() => (thirdAccountDialog.type === 'wechat' ? '微信' : 'QQ'));
  const phoneDialogTitle = computed(() => {
    if (phoneDialog.stage === 'unbind') return '验证原手机号';
    return '绑定新手机';
  });

  const accountSections = computed<AccountSection[]>(() => [
    {
      title: '手机号与密码',
      items: [
        {
          key: 'phone',
          icon: 'settings-phone',
          title: '手机号',
          description: accountInfo.phone
            ? `当前绑定：${formatPhone(accountInfo.phone)}`
            : '绑定手机后，可以更方便的登录、管理账号',
          actionText: accountInfo.phone ? '更换手机' : '立即绑定',
          onClick: openPhoneDialog
        },
        {
          key: 'password',
          icon: 'settings-password',
          title: '密码',
          description: '设置独一无二的密码来保护你的账号安全',
          actionText: accountInfo.hasPassword ? '更换密码' : '立即设置',
          onClick: () => {
            if (accountInfo.hasPassword) {
              passwordDialog.visible = true;
              return;
            }

            void openPasswordSetup();
          }
        }
      ]
    },
    {
      title: '绑定第三方账号',
      items: [
        {
          key: 'wechat',
          icon: 'settings-wechat',
          iconClass: 'wechat',
          title: '微信',
          description: accountInfo.wechatBound
            ? accountInfo.wechatName
              ? `已绑定微信账号：${accountInfo.wechatName}`
              : '已绑定微信，可以通过微信扫码登录'
            : '绑定微信后，可以通过微信扫码登录',
          actionText: accountInfo.wechatBound ? '解除绑定' : '立即绑定',
          initializing: thirdInfoLoading.value,
          loading: thirdPartyLoading.wechat,
          onClick: () => handleThirdAccount('wechat')
        },
        {
          key: 'qq',
          icon: 'settings-qq',
          iconClass: 'qq',
          title: 'QQ',
          description: accountInfo.qqBound
            ? accountInfo.qqName
              ? `已绑定QQ账号：${accountInfo.qqName}`
              : '已绑定QQ，可以通过QQ授权登录'
            : '绑定QQ后，可以通过QQ授权登录',
          actionText: accountInfo.qqBound ? '解除绑定' : '立即绑定',
          initializing: thirdInfoLoading.value,
          loading: thirdPartyLoading.qq,
          onClick: () => handleThirdAccount('qq')
        }
      ]
    }
  ]);

  onMounted(() => {
    void initializeAccountPage();
  });

  onUnmounted(() => {
    clearCountdown();
    stopThirdAccountPolling();
    closeThirdAccountAuthWindow();
  });

  async function initializeAccountPage() {
    try {
      await loadThirdInfo();
    } finally {
      thirdInfoLoading.value = false;
    }
  }

  async function loadThirdInfo({ silent = false }: { silent?: boolean } = {}) {
    try {
      const response = (await userApis.getThirdInfo().req) as ApiResponse<ThirdAccountInfo>;
      accountInfo.wechatBound = Boolean(response.data?.mp_openid?.trim());
      accountInfo.wechatName = accountInfo.wechatBound ? response.data?.mp_name?.trim() || '' : '';
      accountInfo.qqBound = Boolean(response.data?.qq_openid?.trim());
      accountInfo.qqName = accountInfo.qqBound ? response.data?.qq_name?.trim() || '' : '';
    } catch (error) {
      if (!silent) {
        ElMessage.error(getErrorMessage(error, '第三方账号信息加载失败，请稍后重试'));
      }
    }
  }

  function sendSms() {
    const target = phoneDialog;
    const phone = getValidatedPhone();

    if (!phone) {
      ElMessage.warning('请输入正确的新手机号');
      return;
    }

    target.sendingCode = true;
    Object.assign(phoneCaptcha, { account: '', areaCode: 0, verified: false });

    const account = phone.nationalNumber;
    const areaCode = Number(phoneDialog.areaCode);
    const captcha = window.tncode;
    captcha.init();
    captcha.setCheckData(() => ({ account, areaCode, operateId: CHANGE_PHONE_OPERATE_ID }));
    captcha.onsuccess(() => {
      Object.assign(phoneCaptcha, { account, areaCode, verified: true });
      target.sendingCode = false;
      ElMessage.success('验证码已发送');
      startCountdown(target);
    });
    captcha.onerror((responseText) => {
      target.sendingCode = false;
      if (isCaptchaErrorCode(responseText, 'MERGE_ACCOUT')) {
        ElMessage.error('绑定失败，该手机号已注册账号');
      } else if (isCaptchaErrorCode(responseText, 'SEND_RATE_LIMIT')) {
        ElMessage.error('绑定失败，当前IP发送验证码已达上限');
      } else {
        ElMessage.error('请求失败');
      }
    });
    captcha.onhide(() => {
      document.getElementById('tncode_div')?.style.setProperty('display', 'none');
      target.sendingCode = false;
    });
    captcha.show.call(document.createElement('span'));
  }

  function openPhoneDialog() {
    phoneDialog.mode = accountInfo.phone ? 'change' : 'bind';
    phoneDialog.stage = accountInfo.phone ? 'unbind' : 'bind';
    initializePhoneCountry();
    phoneDialog.visible = true;
  }

  async function sendUnbindPhoneCode() {
    if (!accountInfo.phone) {
      ElMessage.warning('当前账号未绑定手机号');
      return;
    }

    phoneUnbind.sendingCode = true;
    try {
      await userApis.getUnbindPhoneCode().req;
      ElMessage.success('验证码已发送至原手机号');
      startCountdown(phoneUnbind);
    } catch (error) {
      ElMessage.error(getErrorMessage(error, '验证码发送失败，请稍后重试'));
    } finally {
      phoneUnbind.sendingCode = false;
    }
  }

  async function verifyUnbindPhoneCode() {
    const code = phoneUnbind.code.trim();
    if (!code) {
      ElMessage.warning('请输入原手机号验证码');
      return;
    }

    phoneUnbind.submitting = true;
    try {
      const response = (await userApis.checkUnbindPhoneCode({ verifyCode: code })
        .req) as ApiResponse<PhoneUnbindCheckData>;
      const checkCode = response.data?.checkCode?.trim();
      if (!checkCode) {
        throw new Error('原手机号验证凭证获取失败，请稍后重试');
      }

      clearCountdown();
      phoneUnbind.countdown = 0;
      phoneUnbind.checkCode = checkCode;
      phoneDialog.stage = 'bind';
      ElMessage.success('原手机号验证成功，请绑定新手机号');
    } catch (error) {
      ElMessage.error(getErrorMessage(error, '原手机号验证失败，请检查验证码'));
    } finally {
      phoneUnbind.submitting = false;
    }
  }

  async function submitChangePhone() {
    const phone = getValidatedPhone();
    const code = phoneDialog.code.trim();

    if (!phone) {
      ElMessage.warning('请输入正确的新手机号');
      return;
    }

    if (!code) {
      ElMessage.warning('请输入验证码');
      return;
    }

    if (
      !phoneCaptcha.verified ||
      phoneCaptcha.account !== phone.nationalNumber ||
      phoneCaptcha.areaCode !== Number(phoneDialog.areaCode)
    ) {
      ElMessage.warning('请先完成滑块验证并获取验证码');
      return;
    }

    if (phoneDialog.mode === 'change' && !phoneUnbind.checkCode) {
      ElMessage.warning('请先验证原手机号');
      return;
    }

    phoneDialog.submitting = true;
    try {
      const newPhone = phone.nationalNumber;
      await userApis.changeBindPhone({
        account: newPhone,
        areaCode: Number(phoneDialog.areaCode),
        verifyCode: code,
        checkCode: phoneDialog.mode === 'change' ? phoneUnbind.checkCode : undefined
      }).req;
      accountInfo.phone = newPhone;
      userStore.userData.mobilePhone = newPhone;
      userStore.userData.phone = newPhone;
      userStore.userData.areaCode = phoneDialog.areaCode;
      userStore.userData.verifyPhone = 1;
      phoneDialog.visible = false;
      ElMessage.success(phoneDialog.mode === 'change' ? '手机号更换成功' : '手机号绑定成功');
    } catch (error) {
      ElMessage.error(getErrorMessage(error, '手机号更换失败，请稍后重试'));
    } finally {
      phoneDialog.submitting = false;
    }
  }

  async function submitChangePassword() {
    const oldPassword = passwordDialog.oldPassword.trim();
    const newPassword = passwordDialog.newPassword.trim();
    const confirmPassword = passwordDialog.confirmPassword.trim();

    if (accountInfo.hasPassword && !oldPassword) {
      ElMessage.warning('请输入原密码');
      return;
    }

    if (newPassword.length < 6 || newPassword.length > 20) {
      ElMessage.warning('新密码长度需要为 6-20 位');
      return;
    }

    if (newPassword !== confirmPassword) {
      ElMessage.warning('两次密码输入不一致');
      return;
    }

    passwordDialog.submitting = true;
    try {
      await userApis.updatePassword({ oldPass: oldPassword, newPass: newPassword }).req;
      accountInfo.hasPassword = true;
      userStore.userData.hasPassword = 1;
      passwordDialog.visible = false;
      ElMessage.success('密码设置成功');
    } catch (error) {
      ElMessage.error(getErrorMessage(error, '密码设置失败，请稍后重试'));
    } finally {
      passwordDialog.submitting = false;
    }
  }

  async function openPasswordSetup() {
    const configured = await phoneBindingGuardRef.value?.openPasswordSetup(accountInfo.phone);
    if (!configured) return;

    accountInfo.phone = userStore.userData.mobilePhone || userStore.userData.phone || '';
    accountInfo.hasPassword = true;
  }

  async function handleThirdAccount(type: ThirdAccountKey) {
    const isWechat = type === 'wechat';
    const isBound = isWechat ? accountInfo.wechatBound : accountInfo.qqBound;

    if (isBound) {
      if (!canUnbindThirdAccount(type)) {
        await ElMessageBox.alert('当前只有一种登录方式，解绑将无法正常登录账号', '无法解除绑定', {
          type: 'warning',
          confirmButtonText: '我知道了'
        }).catch(() => undefined);
        return;
      }

      try {
        await ElMessageBox.confirm(
          `解绑后将无法再使用${isWechat ? '微信' : 'QQ'}登录，确认解除绑定吗？`,
          `解除绑定${isWechat ? '微信' : 'QQ'}`,
          {
            type: 'warning',
            confirmButtonText: '确认解绑',
            cancelButtonText: '取消'
          }
        );
      } catch {
        return;
      }
    }

    const qqAuthWindow = !isWechat && !isBound ? openThirdAccountAuthWindow() : undefined;

    if (!isWechat && !isBound && !qqAuthWindow) {
      ElMessage.error('浏览器阻止了QQ授权窗口，请允许弹窗后重试');
      return;
    }

    thirdPartyLoading[type] = true;
    try {
      const response = (await userApis.operateThirdAccount({
        operate: isBound ? THIRD_OPERATE_UNBIND : THIRD_OPERATE_BIND,
        type: isWechat ? THIRD_TYPE_WECHAT : THIRD_TYPE_QQ
      }).req) as ApiResponse<ThirdAccountBindData>;

      if (isBound) {
        setThirdAccountBound(type, false);
        await loadThirdInfo({ silent: true });
        ElMessage.success(`${isWechat ? '微信' : 'QQ'}已解除绑定`);
        return;
      }

      const url = response.data?.url?.trim() || '';

      if (!isWechat) {
        const authUrl = normalizeThirdAccountUrl(url);
        if (!authUrl) {
          throw new Error('QQ授权地址获取失败，请稍后重试');
        }

        if (!qqAuthWindow || isThirdAccountAuthWindowClosed(qqAuthWindow)) {
          throw new Error('QQ授权窗口已关闭，请重新绑定');
        }

        qqAuthWindow.location.href = authUrl;
        qqAuthWindow.focus();
        startThirdAccountAuthWatcher(qqAuthWindow);
        return;
      }

      const qrUrl = url;
      const sessionId = response.data?.sessionId;
      if (!qrUrl || !sessionId) {
        throw new Error('绑定二维码获取失败，请稍后重试');
      }

      const expireSeconds = toPositiveNumber(
        response.data?.expireTime,
        DEFAULT_THIRD_QR_EXPIRE_SECONDS
      );
      Object.assign(thirdAccountDialog, {
        visible: true,
        type,
        qrUrl,
        sessionId,
        expiresAt: Date.now() + expireSeconds * 1000,
        expired: false
      });
      scheduleThirdAccountPoll();
    } catch (error) {
      if (!isWechat && !isBound) {
        closeThirdAccountAuthWindow(qqAuthWindow ?? null);
      }

      ElMessage.error(
        getErrorMessage(error, isBound ? '解除绑定失败，请稍后重试' : '绑定失败，请稍后重试')
      );
    } finally {
      thirdPartyLoading[type] = false;
    }
  }

  function openThirdAccountAuthWindow() {
    closeThirdAccountAuthWindow();

    const popupWidth = 760;
    const popupHeight = 720;
    const left = Math.max(0, window.screenX + (window.outerWidth - popupWidth) / 2);
    const top = Math.max(0, window.screenY + (window.outerHeight - popupHeight) / 2);
    const features = [
      `width=${popupWidth}`,
      `height=${popupHeight}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
      'resizable=yes',
      'scrollbars=yes'
    ].join(',');

    thirdAccountAuthWindow = window.open('', 'qqBindWindow', features);
    return thirdAccountAuthWindow;
  }

  function startThirdAccountAuthWatcher(authWindow: Window) {
    clearThirdAccountAuthTimer();
    thirdAccountAuthWindow = authWindow;
    thirdAccountAuthTimer = setInterval(() => {
      if (!isThirdAccountAuthWindowClosed(authWindow)) return;

      clearThirdAccountAuthTimer();
      if (thirdAccountAuthWindow === authWindow) {
        thirdAccountAuthWindow = null;
      }
      void loadThirdInfo();
    }, 1000);
  }

  function closeThirdAccountAuthWindow(authWindow = thirdAccountAuthWindow) {
    clearThirdAccountAuthTimer();

    if (authWindow && !isThirdAccountAuthWindowClosed(authWindow)) {
      authWindow.close();
    }

    if (!authWindow || thirdAccountAuthWindow === authWindow) {
      thirdAccountAuthWindow = null;
    }
  }

  function clearThirdAccountAuthTimer() {
    if (thirdAccountAuthTimer) {
      clearInterval(thirdAccountAuthTimer);
      thirdAccountAuthTimer = undefined;
    }
  }

  function isThirdAccountAuthWindowClosed(authWindow: Window) {
    try {
      return authWindow.closed;
    } catch {
      return true;
    }
  }

  function scheduleThirdAccountPoll() {
    clearThirdAccountPollTimer();
    thirdAccountPollTimer = setTimeout(() => {
      void pollThirdAccountProgress();
    }, THIRD_POLL_INTERVAL);
  }

  async function pollThirdAccountProgress() {
    const { sessionId, type } = thirdAccountDialog;
    if (!thirdAccountDialog.visible || !sessionId) return;

    if (Date.now() >= thirdAccountDialog.expiresAt) {
      thirdAccountDialog.expired = true;
      return;
    }

    const request = userApis.loginProgress({
      'X-Fsh5-Sessionid': sessionId,
      getCode: 0
    });
    cancelThirdAccountPollRequest = request.cancel;

    try {
      await request.req;
      if (!isCurrentThirdAccountSession(sessionId)) return;

      stopThirdAccountPolling();
      setThirdAccountBound(type, true);
      await loadThirdInfo({ silent: true });
      thirdAccountDialog.visible = false;
      ElMessage.success(`${type === 'wechat' ? '微信' : 'QQ'}绑定成功`);
    } catch (error) {
      if (!isCurrentThirdAccountSession(sessionId)) return;

      if (isTerminalThirdAccountError(error)) {
        stopThirdAccountPolling();
        thirdAccountDialog.visible = false;
        ElMessage.error(getThirdAccountPollErrorMessage(error));
        return;
      }

      scheduleThirdAccountPoll();
    } finally {
      if (thirdAccountDialog.sessionId === sessionId) {
        cancelThirdAccountPollRequest = undefined;
      }
    }
  }

  function stopThirdAccountPolling() {
    clearThirdAccountPollTimer();
    cancelThirdAccountPollRequest?.();
    cancelThirdAccountPollRequest = undefined;
  }

  function clearThirdAccountPollTimer() {
    if (thirdAccountPollTimer) {
      clearTimeout(thirdAccountPollTimer);
      thirdAccountPollTimer = undefined;
    }
  }

  function isCurrentThirdAccountSession(sessionId: string) {
    return thirdAccountDialog.visible && thirdAccountDialog.sessionId === sessionId;
  }

  function isTerminalThirdAccountError(error: unknown) {
    if (!isStringRecord(error)) return false;
    return ['ERROR', 'LOGIN_SIGNOUT', 'LOGIN_TIMEOUT', 'ACCOUNT_SUSPENDED'].includes(
      String(error.code)
    );
  }

  function getThirdAccountPollErrorMessage(error: unknown) {
    if (isStringRecord(error) && String(error.msg).trim() === '账号已绑定') {
      return '该微信已绑定其他账号，无法重复绑定。请更换微信后重试，或先在原账号中解除绑定。';
    }

    return getErrorMessage(error, '绑定失败，请稍后重试');
  }

  function setThirdAccountBound(type: ThirdAccountKey, bound: boolean) {
    if (type === 'wechat') {
      accountInfo.wechatBound = bound;
      if (!bound) accountInfo.wechatName = '';
    } else {
      accountInfo.qqBound = bound;
      if (!bound) accountInfo.qqName = '';
    }
  }

  function canUnbindThirdAccount(type: ThirdAccountKey) {
    const hasPhoneLogin = Boolean(accountInfo.phone.trim());
    const hasOtherThirdAccount = type === 'wechat' ? accountInfo.qqBound : accountInfo.wechatBound;
    return hasPhoneLogin || hasOtherThirdAccount;
  }

  function startCountdown(target: CountdownState) {
    clearCountdown();
    target.countdown = SMS_COUNTDOWN_SECONDS;
    smsTimer = setInterval(() => {
      target.countdown -= 1;
      if (target.countdown <= 0) {
        clearCountdown();
      }
    }, 1000);
  }

  function clearCountdown() {
    if (smsTimer) {
      clearInterval(smsTimer);
      smsTimer = undefined;
    }
  }

  function resetPhoneDialog() {
    clearCountdown();
    if (document.getElementById('tncode_div')) {
      window.tncode.hide();
    }
    Object.assign(phoneDialog, {
      stage: 'bind',
      mode: 'bind',
      areaCode: DEFAULT_AREA_CODE,
      countryCode: DEFAULT_PHONE_COUNTRY,
      defaultCountry: DEFAULT_PHONE_COUNTRY,
      newPhone: '',
      code: '',
      countdown: 0,
      sendingCode: false,
      submitting: false
    });
    Object.assign(phoneUnbind, {
      code: '',
      checkCode: '',
      countdown: 0,
      sendingCode: false,
      submitting: false
    });
    Object.assign(phoneCaptcha, { account: '', areaCode: 0, verified: false });
  }

  function initializePhoneCountry() {
    const areaCode = userStore.userData.areaCode.replace(/^\+/, '') || DEFAULT_AREA_CODE;
    const numericAreaCode = Number(areaCode);
    phoneDialog.areaCode = areaCode;
    phoneDialog.countryCode = areaCode === DEFAULT_AREA_CODE ? DEFAULT_PHONE_COUNTRY : '';
    phoneDialog.defaultCountry = Number.isFinite(numericAreaCode)
      ? numericAreaCode
      : DEFAULT_PHONE_COUNTRY;
    phoneDialog.inputKey += 1;
  }

  function handlePhoneCountryChanged(country: CountryObject) {
    phoneDialog.areaCode = country.dialCode;
    phoneDialog.countryCode = country.iso2;
  }

  function getValidatedPhone() {
    const phone = phoneDialog.newPhone.trim();
    if (!phone || !phoneDialog.countryCode) return undefined;

    const parsedPhone = parsePhoneNumberFromString(phone, phoneDialog.countryCode as CountryCode);
    return parsedPhone?.isValid() ? parsedPhone : undefined;
  }

  function resetPasswordDialog() {
    Object.assign(passwordDialog, {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
      submitting: false
    });
  }

  function resetThirdAccountDialog() {
    stopThirdAccountPolling();
    Object.assign(thirdAccountDialog, {
      qrUrl: '',
      sessionId: '',
      expiresAt: 0,
      expired: false
    });
  }

  function formatPhone(phone: string) {
    return phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');
  }

  function toPositiveNumber(value: unknown, fallback: number) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
  }

  function normalizeThirdAccountUrl(url: string) {
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  function isCaptchaErrorCode(responseText: string, code: string) {
    try {
      const response = JSON.parse(responseText) as { code?: unknown };
      return response.code === code;
    } catch {
      return false;
    }
  }

  function getErrorMessage(error: unknown, fallback: string) {
    if (isStringRecord(error)) {
      if (typeof error.errorMsg === 'string' && error.errorMsg) return error.errorMsg;
      if (typeof error.msg === 'string' && error.msg) return error.msg;
      if (typeof error.message === 'string' && error.message) return error.message;
    }

    return fallback;
  }

  function isStringRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
</script>

<style scoped>
  .account-page {
    width: 620px;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-sizing: border-box;
  }

  .account-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .account-section + .account-section {
    margin-top: 0;
  }

  .section-title {
    margin: 0;
    color: #333940;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
  }

  .account-card-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .account-card {
    width: 100%;
    min-height: 76px;
    padding: 14px 16px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
  }

  .account-card-main {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  .account-icon-wrap {
    width: 40px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-sizing: border-box;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #333940;
  }

  .account-icon-wrap.wechat,
  .account-icon-wrap.qq {
    color: inherit;
  }

  .account-copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .account-title {
    color: #333940;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
  }

  .account-description {
    color: #999999;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .account-description-placeholder {
    width: min(220px, 100%);
    height: 14px;
    margin: 5px 0;
    border-radius: 2px;
    background: #f0f2f5;
  }

  .account-action {
    width: 80px;
    height: 40px;
    padding: 0 12px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    color: #333940;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
    cursor: pointer;
    transition:
      border-color 0.15s ease,
      color 0.15s ease,
      background 0.15s ease;
  }

  .account-action-loading {
    width: 80px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: #909399;
    font-size: 18px;
  }

  .account-action:hover:not(:disabled) {
    border-color: #c7d2fe;
    color: #4c4cef;
    background: #f7f7ff;
  }

  .account-action:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  .sms-row {
    width: 100%;
    display: flex;
    gap: 8px;
  }

  .phone-number-input {
    width: 100%;
    height: 40px;
    box-sizing: border-box;
    border-color: #1677ff;
    border-radius: 6px;
    box-shadow: none;
    transition: border-color 0.2s;
  }

  .phone-number-input:hover {
    border-color: #1677ff;
  }

  .phone-number-input:focus-within {
    border-color: #1677ff;
    box-shadow: 0 0 0 1px #1677ff inset;
  }

  .phone-number-input :deep(.vti__dropdown) {
    width: 74px;
    min-width: 74px;
    padding: 0 12px;
    box-sizing: border-box;
    border-right: 1px solid #e5e7eb;
  }

  .phone-number-input :deep(.vti__selection) {
    width: 100%;
    justify-content: space-between;
    color: #303133;
    font-size: 14px;
  }

  .phone-number-input :deep(.vti__dropdown-arrow) {
    margin-left: 6px;
    color: #a8abb2;
  }

  .phone-number-input :deep(.vti__dropdown-list) {
    z-index: 10;
    width: 312px !important;
    max-width: calc(100vw - 48px);
  }

  .phone-number-input :deep(.vti__input) {
    min-width: 0;
    height: 38px;
    padding: 0 8px;
    color: #303133;
    font-size: 14px;
  }

  .phone-number-input :deep(.vti__input::placeholder) {
    color: #a8abb2;
  }

  .phone-binding-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 16px 8px 8px;
  }

  .phone-binding-description {
    margin: 0;
    color: #303133;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  .phone-binding-sms-row {
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 96px;
    gap: 8px;
  }

  .phone-binding-code {
    height: 40px;
  }

  .phone-binding-code :deep(.el-input__wrapper) {
    padding: 0 12px;
    border-radius: 6px;
    background: #f5f7fa;
    box-shadow: 0 0 0 1px #dcdfe6 inset;
  }

  .phone-binding-code :deep(.el-input__inner) {
    color: #303133;
    font-size: 14px;
  }

  .phone-binding-code :deep(.el-input__inner::placeholder) {
    color: #c0c4cc;
  }

  .phone-binding-sms-button,
  .phone-binding-submit {
    height: 40px;
    margin: 0;
    border-radius: 6px;
    font-size: 14px;
  }

  .phone-binding-sms-button {
    width: 96px;
    padding: 0 8px;
    color: #909399;
  }

  .phone-binding-submit {
    width: 100%;
    border-color: #1677ff;
    background: #1677ff;
    font-weight: 500;
  }

  :global(.phone-binding-dialog .el-dialog__footer) {
    display: none;
  }

  .sms-button {
    width: 128px;
    flex-shrink: 0;
  }

  .phone-verification-alert {
    margin-bottom: 18px;
  }

  .third-account-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  .third-account-qrcode {
    position: relative;
    width: 220px;
    height: 220px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
  }

  .third-account-qrcode img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .third-account-expired {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: rgb(255 255 255 / 92%);
    color: #333940;
    font-size: 14px;
    line-height: 22px;
  }

  .third-account-expired strong {
    font-size: 16px;
  }

  .third-account-expired span,
  .third-account-content p span {
    color: #999999;
  }

  .third-account-content p {
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #333940;
    font-size: 14px;
    line-height: 22px;
  }

  .account-page--mobile {
    width: 100%;
    max-width: none;
    min-height: 100%;
    gap: 24px;
    padding: 16px 16px 18px;
    background: #ffffff;
  }

  .account-page--mobile .account-section {
    gap: 12px;
  }

  .account-page--mobile .account-card-list {
    gap: 12px;
  }

  .account-page--mobile .account-card {
    min-height: 76px;
    align-items: center;
    flex-direction: row;
    gap: 12px;
    padding: 14px 0;
    border-color: #eeeeee;
    border-right: 0;
    border-left: 0;
    border-radius: 0;
  }

  .account-page--mobile .account-action {
    align-self: center;
    width: 80px;
    height: 40px;
    border-color: #eeeeee;
    font-weight: 700;
  }

  .account-page--mobile .account-icon-wrap {
    width: 40px;
    height: 40px;
    border-color: #eeeeee;
    border-radius: 8px;
  }

  .account-page--mobile .account-title {
    line-height: 22px;
  }

  .account-page--mobile .account-description {
    line-height: 22px;
  }

  :global(.settings-account-dialog--mobile) {
    width: calc(100vw - 32px) !important;
    max-width: 400px;
  }

  :global(.settings-account-dialog--mobile.phone-binding-dialog) {
    max-width: 360px;
  }

  :global(.settings-account-dialog--mobile .sms-row) {
    flex-direction: column;
  }

  :global(.settings-account-dialog--mobile .sms-button) {
    width: 100%;
  }
</style>
